/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

/**
 * @module bulb-image-carousel/bulbimagecarouselediting
 */

import { Plugin, ViewElement, Widget } from 'ckeditor5';
import type { ModelElement, ViewDowncastWriter } from 'ckeditor5';

import { InsertBulbImageCarouselCommand } from './bulbimagecarouselcommand';
import { toImageCarouselWidget } from './bulbimagecarouselutils';
import { getBlockVersion } from '../utils';

/**
 * The bulb image carousel editing infrastructure, this will create the block using the converters.
 *
 * @extends Plugin
 */
export default class BulbImageCarouselEditing extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [Widget] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageCarouselEditing' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    this.defineSchema();
    this.defineConverters();

    this.editor.commands.add('insertImageCarousel', new InsertBulbImageCarouselCommand(this.editor));
  }

  /**
   * Method that defines the schema four our image carousel block.
   *
   * @private
   */
  private defineSchema(): void {
    const { schema } = this.editor.model;

    schema.register('imageCarousel', {
      isObject: true,
      isBlock: true,
      allowWhere: '$block',
      allowAttributes: ['bulb-block-id', 'blockAlignment', 'bulbCarouselTransition', 'bulb-block-version'],
    });
  }

  /**
   * Method that creates and initializes the downcast ( editing and data ) and
   * upcast converters for our image carousel block.
   *
   * @private
   */
  private defineConverters(): void {
    const { conversion } = this.editor;

    conversion.for('upcast').elementToElement({
      view: {
        name: 'figure',
        classes: 'image-carousel',
      },
      model: (viewElement, { writer: modelWriter }) => modelWriter.createElement('imageCarousel', {
        'bulb-block-id': viewElement.getAttribute('bulb-block-id'),
        bulbCarouselTransition: viewElement.getAttribute('effect') || 'slide',
        'bulb-block-version': getBlockVersion(CKEBulbData, viewElement),
      }),
    });

    conversion.for('dataDowncast').elementToElement({
      model: 'imageCarousel',
      view: (modelElement, { writer: viewWriter }) => viewWriter.createContainerElement('figure', {
        class: 'image-carousel',
        'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
        'bulb-block-version': getBlockVersion(CKEBulbData, modelElement),
        effect: modelElement.getAttribute('bulbCarouselTransition') || 'slide',
      }),
    });

    conversion.for('editingDowncast').elementToElement({
      model: 'imageCarousel',
      view: (
        modelElement,
        { writer: viewWriter },
      ) => toImageCarouselWidget(this.createImageCarouselViewElement(modelElement, viewWriter), viewWriter),
    });
  }

  /**
   *  To create a figure element for image carousel.
   * @param modelElement model element to be written to the view.
   * @param writer : ViewDowncastWriter to use in writing to the ckeditor view.
   * @returns {module:engine/view/element~ViewElement} The figure element representing the image carousel.
   */
  private createImageCarouselViewElement(modelElement: ModelElement, writer: ViewDowncastWriter): ViewElement {
    let figure = null;
    const figureAttribute = {
      class: 'image-carousel no-editor-placeholder',
      'bulb-block-id': modelElement.getAttribute('bulb-block-id'),
      effect: modelElement.getAttribute('bulbCarouselTransition') || 'slide',
      'bulb-block-version': modelElement.getAttribute('bulb-block-version'),
    };

    if (CKEBulbData.page.unit && CKEBulbData.page.unit.contentBlocks) {
      const contentBlock = CKEBulbData.page.unit.contentBlocks[modelElement.getAttribute('bulb-block-id') as string];
      if (contentBlock) {
        const editable = CKEBulbData.page.editing;
        figureAttribute['bulb-block-version'] = contentBlock.version;
        figureAttribute.class += ` ${contentBlock.orientation}`;

        // Change cursor for cube transition in view mode
        if (modelElement.getAttribute('bulbCarouselTransition') === 'cube' && !editable) {
          figureAttribute.class += ' cke-carousel-cube-pointer';
        }
        figure = writer.createContainerElement('figure', figureAttribute);
        const uiElement = writer.createUIElement('div', {
          id: `wrapper-${contentBlock.id}`,
        }, function (domDocument) {
          const domElement = this.toDomElement(domDocument);
          // eslint-disable-next-line max-len
          CKEBulbData.service.componentInjector.injectImageCarousel({
            slides: contentBlock.carouselImages,
            orientation: contentBlock.orientation,
            blockId: contentBlock.id,
            editable,
            source: 'ckeditor',
            effect: modelElement.getAttribute('bulbCarouselTransition'),
          }, domElement, contentBlock.id);
          return domElement;
        });
        writer.insert(writer.createPositionAt(figure, 'end'), uiElement);
      }
    } else {
      figure = writer.createContainerElement('figure', figureAttribute);
    }

    return figure;
  }
}
